<template>
	<div class="integration-con w-100" data-cy="blackbaud-new-component">
		<div class="authorize-row">
			<div class="d-flex justify-space-between align-center" style="width: 100%">
				<span class="title">SKY API Authorized</span>
				<v-switch
					style="width: min-content"
					color="primary"
					class="custom-input checkbox"
					density="compact"
					:loading="loadingData || loadAuthorize"
					:model-value="isConnected"
					data-cy="blackbaud-new-authorize-switch"
					@update:model-value="onAuthorizeToggle"
				></v-switch>
			</div>
		</div>

		<div v-if="isConnected" class="mt-4 sync-section w-100">
			<ecsTaskSync
				integration_type="blackbaud"
				:has-valid-config="isConnected"
				:schools="data?.schools || []"
			/>
		</div>

		<bb-sso-modal
			v-if="data"
			:data="data"
			:show="ssoModal"
			integration-path="blackbaud"
			@update:show="$emit('update:ssoModal', $event)"
			@update:data="$emit('update:data', $event)"
		></bb-sso-modal>
	</div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import type { BlackBaudIntegration } from "@/api_types/school";
import FetchService from "@/services/fetch.service";
import { useAlertsStore } from "@/stores/alerts";
import { useSchoolStore } from "@/stores/school";
import { integrationWindow } from "@/utils/integrationWindow";
import EcsTaskSync from "@/components/school/integration/ecsTaskSync.vue";
import BbSsoModal from "@/components/school/integration/bbSsoModal.vue";
import { isIntegrationConnected } from "@/components/school/integration/utils";

const BASE_URL = import.meta.env.VITE_BASE_URL;

const props = defineProps<{
	data?: BlackBaudIntegration;
	loadingData: boolean;
	ssoModal: boolean;
}>();

const emit = defineEmits<{
	(event: "update:data", val: Partial<BlackBaudIntegration>): void;
	(event: "reload:data"): void;
	(event: "update:ssoModal", val: boolean): void;
}>();

const alertStore = useAlertsStore();
const schoolStore = useSchoolStore();
const loadAuthorize = ref(false);

// API may return boolean, 0/1, or omit the field until OAuth tokens exist
const isConnected = computed(() => isIntegrationConnected(props.data?.is_connected));

const onAuthorizeToggle = (enabled: boolean | null) => {
	if (props.loadingData || loadAuthorize.value) return;
	if (enabled) {
		authorize();
		return;
	}
	emit("update:data", { is_connected: false });
	removeAuthorization();
};

const authorize = async () => {
	const url = `${BASE_URL}/api/oauth2/blackbaud?school_id=${schoolStore.school_id}`;
	loadAuthorize.value = true;

	// Blackbaud's login page clears window.opener, so the success page cannot
	// postMessage back. Poll the saved tokens instead of waiting on that message.
	const res = await integrationWindow(url, "Blackbaud integration enabled successfully", "_black", {
		poll: async () => {
			const data = await FetchService.fetchData("/integration/blackbaud", "get");
			return isIntegrationConnected(data?.is_connected);
		},
	});

	loadAuthorize.value = false;
	if (res.success) {
		emit("reload:data");
	}
};

const removeAuthorization = async () => {
	loadAuthorize.value = true;
	// OAuth tokens live on /integration/blackbaud (shared with legacy)
	const res = await FetchService.fetchData("/integration/blackbaud", "put", {
		is_connected: false,
	});
	if (typeof res === "object") {
		emit("update:data", res);
	}
	if (typeof res === "object" && !res.is_connected) {
		alertStore.addAlert("success", "Blackbaud integration disabled successfully");
	} else {
		alertStore.addAlert("error", "Failed to disabled Blackbaud integration");
		emit("update:data", {
			is_connected: true,
		});
	}
	loadAuthorize.value = false;
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;

.title {
	color: $primary;
	font-weight: 600;
	font-size: 1.25rem;
}
.integration-con {
	margin-top: 30px;
	width: 100%;
	flex: 1 1 100%;
}

.authorize-row {
	width: 350px;
	max-width: 100%;
}

.sync-section {
	width: 100%;

	:deep(.v-card) {
		width: 100%;
	}
}

.checkbox {
	:deep(.v-input__control) {
		.v-selection-control {
			justify-content: flex-end;
		}
	}
}
</style>
